import { useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useAuthStore } from "../../../../console/store/useAuthStore";
import { enum_api_uri } from "../../utils/enum";

interface OrderInfoRequest {
    order_name?: string;
    order_mobile?: string;
    product_idx?: number;
    sales_amount?: number;
    order_amount?: number;
    delivery_idx?: number | null;
    delivery_amount?: number;
    wreath_sender?: string;
    wreath_word?: string;
    recipient_name?: string;
    recipient_mobile?: string;
    is_union?: boolean;
    event_content_idx?: number;
    related_person?: string;
    work_location?: string;
    department?: string;
    company_tel?: string | null;
    delivery_immediate?: boolean;
    delivery_date?: string;
    delivery_time?: string;
    delivery_post?: string;
    delivery_address?: string;
    delivery_address_detail?: string;
    delivery_memo_idx?: number | null;
    delivery_memo?: string;
    file_memo?: string;
    order_memo?: string;
    del_file_idx?: number[];
    ready_image?: File[];
    delivered_image?: File[];
    order_file?: File[];
    cancel_reason_idx?: number | null;
    cancel_reason?: string;
    event_content?: string;
    branch_type?: number;
    customer_branch_idx?: number;
    branch_idx?: number;
    branch_req?: string;
    branch_product_idx?: number[];
    branch_product_del_file_idx?: number[];
    branch_product_image?: File[];
    customer_manager_idx?: number[] | null;
    customer_idx?: number;
    customer_name?: string;
    customer_request?: string;
    product_quantity?: number;
}

interface ApiErrorResponse {
    statusCode: number;
    message: string;
}

export const usePutOrderInfo = (
    successCallback?: () => void,
    order_idx?: number,
): UseMutationResult<null, AxiosError<ApiErrorResponse>, OrderInfoRequest, unknown> => {
    const queryClient = useQueryClient();
    const accessToken = useAuthStore(state => state.accessToken);

    return useMutation<null, AxiosError<ApiErrorResponse>, OrderInfoRequest>({
        mutationFn: async (data: OrderInfoRequest) => {
            try {
                const formData = new FormData();

                Object.entries(data).forEach(([key, value]) => {
                    if (value !== undefined) {
                        if (typeof value === "number" && isNaN(value)) {
                            formData.append(key, "null");
                            return;
                        }
                        if (Array.isArray(value)) {
                            value.forEach(item => {
                                if (item instanceof File) {
                                    formData.append(key, item);
                                } else {
                                    formData.append(key, JSON.stringify(item));
                                }
                            });
                        } else {
                            formData.append(key, value as string | Blob);
                        }
                    }
                });

                const response = await axios.put(`${enum_api_uri.put_order_info}/${order_idx}`, formData, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "multipart/form-data",
                    },
                });

                return response.data.data;
            } catch (error) {
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getOrderList"] });
            queryClient.invalidateQueries({ queryKey: ["getOrderInfo"] });
            queryClient.invalidateQueries({ queryKey: ["getOrderHistory"] });

            successCallback?.();
        },
    });
};
